import { createSlice } from "@reduxjs/toolkit"
import {
  bookingRequest,
  cancelBooking,
  confirmBooking,
  createPricelist,
  deleteBooking,
  deletePricelist,
  fetchAvailableStatusTransitions,
  fetchBookingById,
  fetchBookings,
  fetchPricelists,
  patchBooking,
  updateBooking,
  updateBookingStatus,
  updateConfirmationStatus,
  updateDocumentStatus
} from "../actions/booking/booking-action"
import {
  assignDriverToSchedule,
  cancelTrip,
  completeTrip,
  downloadInvoice,
  DriverSchedules,
  endSchedule,
  fetchDriverDashboard,
  fetchInvoiceById,
  fetchInvoices,
  fetchScheduleById,
  fetchSchedules,
  fetchTripById,
  fetchTrips,
  getDriverSchedules,
  getTerminationDetails,
  sendInvoiceReminder,
  startSchedule,
  startTrip,
  terminateSchedule,
  updateSchedule,
  updateSchedules
} from "../actions/schedules/schedules-action"

const initialState = {
  bookings: [],
  pricelists: [],
  currentBooking: null,
  schedules: [],
  trips: [],
  invoices: [],
  currentInvoice: null,
  driverDashboard: null,
  // driverSchedules: null,
  statusUpdating: {
    booking: false,
    confirmation: false,
    document: false
  },

  loading: false,
  error: null,
  success: false,
  message: "",
  statusTransitions: {},
  userPriceListAssignments: {}
}

const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    resetBookingState: () => initialState,
    clearCurrentBooking: (state) => {
      state.currentBooking = null
    },
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null
    },
    updateBookingOptimistic: (state, action) => {
      const { bookingId, type, value } = action.payload

      state.bookings = state.bookings.map((booking) => {
        if (booking.id === bookingId) {
          switch (type) {
            case "booking":
              return {
                ...booking,
                booking_status: value,
                status: value
              }
            case "confirmation":
              return {
                ...booking,
                confirmation_status: value
              }
            case "document":
              return {
                ...booking,
                document_status: value
              }
            default:
              return booking
          }
        }
        return booking
      })
    },
    revertBookingStatus: (state, action) => {
      const { bookingId, type, previousValue } = action.payload

      state.bookings = state.bookings.map((booking) => {
        if (booking.id === bookingId) {
          switch (type) {
            case "booking":
              return {
                ...booking,
                booking_status: previousValue,
                status: previousValue
              }
            case "confirmation":
              return {
                ...booking,
                confirmation_status: previousValue
              }
            case "document":
              return {
                ...booking,
                document_status: previousValue
              }
            default:
              return booking
          }
        }
        return booking
      })
    },

    updateUserPriceListAssignments: (state, action) => {
      const { userId, priceListId } = action.payload
      state.userPriceListAssignments[userId] = priceListId
    },
    clearStatusTransitions: (state) => {
      state.statusTransitions = {}
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchBookings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false
        state.bookings = action.payload
        state.success = true
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchBookingById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.loading = false
        state.currentBooking = action.payload
        state.success = true
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(bookingRequest.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(bookingRequest.fulfilled, (state, action) => {
        state.loading = false
        state.bookings.push(action.payload)
        state.success = true
        state.message = "Booking created successfully"
      })
      .addCase(bookingRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      // Update booking
      .addCase(updateBooking.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.loading = false
        state.bookings = state.bookings.map((booking) => {
          if (booking.id === action.payload.id) {
            const statusUpdates = {}
            if (action.payload.booking_status !== booking.booking_status) {
              statusUpdates.booking_status = booking.booking_status
              statusUpdates.status = booking.status // Keep the generic status in sync
            }
            if (
              action.payload.confirmation_status !== booking.confirmation_status
            ) {
              statusUpdates.confirmation_status = booking.confirmation_status
            }
            if (action.payload.document_status !== booking.document_status) {
              statusUpdates.document_status = booking.document_status
            }

            return {
              ...booking,
              ...action.payload,
              ...statusUpdates
            }
          }
          return booking
        })
        state.success = true
        state.message = "Booking updated successfully"
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
        console.error("Update rejected:", action.payload)
      })

      // Delete booking
      .addCase(deleteBooking.fulfilled, (state, action) => {
        state.loading = false
        state.bookings = state.bookings.filter(
          (booking) => booking.id !== action.payload
        )
        state.success = true
        state.message = "Booking deleted successfully"
      })

      // Confirm booking
      .addCase(confirmBooking.fulfilled, (state, action) => {
        state.loading = false
        state.bookings = state.bookings.map((booking) =>
          booking.id === action.payload.id ? action.payload : booking
        )
        state.success = true
        state.message = "Booking confirmed successfully"
      })

      // Cancel booking
      .addCase(cancelBooking.fulfilled, (state, action) => {
        state.loading = false
        state.bookings = state.bookings.map((booking) =>
          booking.id === action.payload.id ? action.payload : booking
        )
        state.success = true
        state.message = "Booking cancelled successfully"
      })

      //patching
      .addCase(patchBooking.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(patchBooking.fulfilled, (state, action) => {
        state.loading = false
        const serverResponse = action.payload

        state.bookings = state.bookings.map((booking) => {
          if (booking.id === serverResponse.id) {
            const currentOptimisticValues = {
              booking_status: booking.booking_status,
              status: booking.status,
              confirmation_status: booking.confirmation_status,
              document_status: booking.document_status
            }

            const updatedBooking = {
              ...serverResponse,
              booking_status:
                currentOptimisticValues.booking_status ||
                serverResponse.booking_status,
              status:
                currentOptimisticValues.status || serverResponse.booking_status,
              confirmation_status:
                currentOptimisticValues.confirmation_status ||
                serverResponse.confirmation_status,
              document_status:
                currentOptimisticValues.document_status ||
                serverResponse.document_status
            }

            console.log("Preserving optimistic update:", {
              original: currentOptimisticValues,
              server: serverResponse,
              final: updatedBooking
            })

            return updatedBooking
          }
          return booking
        })

        state.success = true
        state.message = "Booking updated successfully"
      })
      .addCase(patchBooking.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(createPricelist.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(createPricelist.fulfilled, (state, action) => {
        state.loading = false
        state.pricelists.push(action.payload)
        state.success = true
        state.error = null
      })
      .addCase(createPricelist.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchPricelists.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPricelists.fulfilled, (state, action) => {
        state.loading = false
        state.pricelists = action.payload
        state.error = null
      })
      .addCase(fetchPricelists.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(deletePricelist.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })

      .addCase(deletePricelist.fulfilled, (state, action) => {
        state.loading = false
        state.pricelists = state.pricelists.filter(
          (pricelist) => pricelist.id !== action.payload
        )
        if (state.currentPricelist?.id === action.payload) {
          state.currentPricelist = null
        }
        state.success = true
        state.error = null
      })
      .addCase(deletePricelist.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(updateBookingStatus.pending, (state) => {
        state.statusUpdating.booking = true
        state.error = null
      })
      .addCase(updateBookingStatus.fulfilled, (state) => {
        state.statusUpdating.booking = false
        state.success = true
        state.message = "Booking status updated successfully"
      })
      .addCase(updateBookingStatus.rejected, (state, action) => {
        state.statusUpdating.booking = false
        state.error = action.payload
        state.success = false
      })

      .addCase(updateConfirmationStatus.pending, (state) => {
        state.statusUpdating.confirmation = true
        state.error = null
      })
      .addCase(updateConfirmationStatus.fulfilled, (state) => {
        state.statusUpdating.confirmation = false
        state.success = true
        state.message = "Confirmation status updated successfully"
      })
      .addCase(updateConfirmationStatus.rejected, (state, action) => {
        state.statusUpdating.confirmation = false
        state.error = action.payload
        state.success = false
      })

      .addCase(updateDocumentStatus.pending, (state) => {
        state.statusUpdating.document = true
        state.error = null
      })
      .addCase(updateDocumentStatus.fulfilled, (state) => {
        state.statusUpdating.document = false
        state.success = true
        state.message = "Document status updated successfully"
      })
      .addCase(updateDocumentStatus.rejected, (state, action) => {
        state.statusUpdating.document = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchAvailableStatusTransitions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAvailableStatusTransitions.fulfilled, (state, action) => {
        state.loading = false
        const { bookingId, transitions } = action.payload
        state.statusTransitions = {
          ...state.statusTransitions,
          [bookingId]: transitions
        }
      })
      .addCase(fetchAvailableStatusTransitions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      //schedulers

      .addCase(assignDriverToSchedule.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(assignDriverToSchedule.fulfilled, (state, action) => {
        state.loading = false
        state.schedules = state.schedules.map((schedule) =>
          schedule.id === action.payload.id ? action.payload : schedule
        )
        if (state.currentSchedule?.id === action.payload.id) {
          state.currentSchedule = action.payload
        }
        state.success = true
        state.message = "Driver assigned successfully"
      })
      .addCase(assignDriverToSchedule.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchSchedules.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.loading = false
        state.schedules = action.payload
        state.success = true
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchScheduleById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchScheduleById.fulfilled, (state, action) => {
        state.loading = false
        state.currentSchedule = action.payload
        state.success = true
      })
      .addCase(fetchScheduleById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(updateSchedule.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        state.loading = false
        state.schedules = state.schedules.map((schedule) =>
          schedule.id === action.payload.id ? action.payload : schedule
        )
        state.success = true
        state.message = "Schedule updated successfully"
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(startSchedule.fulfilled, (state, action) => {
        state.loading = false
        state.schedules = state.schedules.map((schedule) =>
          schedule.id === action.payload.id ? action.payload : schedule
        )
        state.success = true
        state.message = "Schedule started successfully"
      })

      // End schedule
      .addCase(endSchedule.fulfilled, (state, action) => {
        state.loading = false
        state.schedules = state.schedules.map((schedule) =>
          schedule.id === action.payload.id ? action.payload : schedule
        )
        state.success = true
        state.message = "Schedule ended successfully"
      })

      .addCase(terminateSchedule.fulfilled, (state, action) => {
        state.loading = false
        state.schedules = state.schedules.map((schedule) =>
          schedule.id === action.payload.id ? action.payload : schedule
        )
        state.success = true
        state.message = "Schedule terminated successfully"
      })

      //invoices

      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false
        state.invoices = action.payload
        state.success = true
        state.error = null
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false
        state.currentInvoice = action.payload
        state.success = true
        state.error = null
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(sendInvoiceReminder.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(sendInvoiceReminder.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Reminder sent successfully"

        if (action.payload) {
          state.invoices = state.invoices.map((invoice) =>
            invoice.id === action.payload.id ? action.payload : invoice
          )
          if (state.currentInvoice?.id === action.payload.id) {
            state.currentInvoice = action.payload
          }
        }
      })
      .addCase(sendInvoiceReminder.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(downloadInvoice.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(downloadInvoice.fulfilled, (state) => {
        state.loading = false
        state.success = true
        state.message = "Invoice downloaded successfully"
      })
      .addCase(downloadInvoice.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(DriverSchedules.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(DriverSchedules.fulfilled, (state, action) => {
        state.loading = false
        state.trips = action.payload
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(DriverSchedules.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchTrips.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTrips.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(fetchTrips.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchTripById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTripById.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(fetchTripById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(startTrip.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(startTrip.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(startTrip.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(completeTrip.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(completeTrip.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(completeTrip.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(cancelTrip.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(cancelTrip.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(cancelTrip.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(getTerminationDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getTerminationDetails.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(getTerminationDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(updateSchedules.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSchedules.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = "Driver notified successfully"
      })
      .addCase(updateSchedules.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(getDriverSchedules.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getDriverSchedules.fulfilled, (state, action) => {
        state.loading = false
        state.driverSchedules = action.payload
        state.success = true
      })
      .addCase(getDriverSchedules.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchDriverDashboard.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDriverDashboard.fulfilled, (state, action) => {
        state.loading = false
        state.driverDashboard = action.payload
        state.success = true
        state.error = null
      })
      .addCase(fetchDriverDashboard.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })
  }
})

export const {
  resetBookingState,
  clearCurrentBooking,
  clearCurrentInvoice,
  updateBookingOptimistic,
  updateUserPriceListAssignments
} = bookingSlice.actions
export default bookingSlice.reducer
